"use client";

import React, { useRef } from "react";
import { Cpu, FileText, Link2, MapPin, ArrowRight, Layers, Sparkles, CircleCheck as CheckCircle2, TrendingUp } from 'lucide-react';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const pillarsData = [{
  id: "technical",
  number: "01",
  title: "Technical SEO",
  tag: "Core Foundation",
  description: "I minimize crawl friction, optimize site architecture, and improve Core Web Vitals to enable faster and more reliable indexing of your key commercial pages.",
  deliverable: "Architecture & Speed Optimization",
  iconName: "cpu"
}, {
  id: "onpage",
  number: "02",
  title: "On-Page Optimisation",
  tag: "User & Search Intent",
  description: "I align meta structures, internal linking, and UX semantics directly with search intent to effectively target high-value commercial queries.",
  deliverable: "Intent & Conversion Alignment",
  iconName: "file"
}, {
  id: "content",
  number: "03",
  title: "Content Strategy",
  tag: "Topical Authority",
  description: "I map comprehensive content hubs that demonstrate real expertise, addressing key content gaps and establishing topical depth across target subjects.",
  deliverable: "Topical Clustering & Hubs",
  iconName: "layers"
}, {
  id: "links",
  number: "04",
  title: "Link Building & Authority",
  tag: "Domain Trust",
  description: "I execute white-hat link acquisition strategies through genuine outreach and digital PR to strengthen your domain's authority profile over time.",
  deliverable: "High-Authority Digital PR",
  iconName: "link"
}, {
  id: "geo",
  number: "05",
  title: "Local + National Integration",
  tag: "Geographic Reach",
  description: "I bridge targeted regional landing pages with broader nationwide SEO campaigns, strengthening organic visibility across your primary target markets.",
  deliverable: "Geographic Market Expansion",
  iconName: "mappin"
}];
const connectionPoints = [{
  title: "Technical + On-Page Synergy",
  desc: "A performant site structure enables faster, more reliable crawling and indexing for your optimized commercial pages."
}, {
  title: "Content + Authority Flywheel",
  desc: "Deep, authoritative content earns genuine editorial links, steadily building domain trust and organic visibility."
}, {
  title: "Local + National Scaling",
  desc: "Targeted geographic signals complement national campaign authority, capturing intent from regional searchers effectively."
}];
export default function ServicePillars() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    gsap.from(".pillar-card", {
      opacity: 0,
      y: 30,
      stagger: 0.12,
      duration: 0.7,
      ease: "power2.out",
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top 75%"
      }
    });
    gsap.from(".interconnection-block", {
      opacity: 0,
      y: 20,
      duration: 0.8,
      ease: "power2.out",
      scrollTrigger: {
        trigger: ".interconnection-block",
        start: "top 85%"
      }
    });
  }, {
    scope: containerRef
  });
  const getPillarIcon = (iconName: string) => {
    switch (iconName) {
      case "cpu":
        return <Cpu className="size-6 text-primary" />;
      case "file":
        return <FileText className="size-6 text-primary" />;
      case "layers":
        return <Layers className="size-6 text-primary" />;
      case "link":
        return <Link2 className="size-6 text-primary" />;
      case "mappin":
        return <MapPin className="size-6 text-primary" />;
      default:
        return <Sparkles className="size-6 text-primary" />;
    }
  };
  return <section id="servicepillarsgrid" data-nav="light" className="bg-background text-foreground py-20 md:py-28 overflow-hidden relative" ref={containerRef}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium text-xs tracking-wide uppercase bg-secondary text-secondary-foreground border border-border">
            <Sparkles className="size-3.5 mr-1.5 text-accent" />
            The 5 Core Pillars
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            Integrated SEO Pillars for Sustainable Growth
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Sustainable national search visibility requires every discipline operating in sync. Rather than treating each area as an isolated tactic, I integrate technical infrastructure, on-page intent, topical content, and domain authority into one unified engine.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {pillarsData.map((pillar, i) => <div key={pillar.id} data-index={i} className="pillar-card h-full">
              <Card className="h-full bg-card border-border shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between group rounded-xl">
                <CardHeader className="p-6 md:p-8 pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-lg bg-muted text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                      {getPillarIcon(pillar.iconName)}
                    </div>
                    <span className="text-2xl font-bold font-mono text-muted-foreground/50 group-hover:text-accent transition-colors duration-300">
                      {pillar.number}
                    </span>
                  </div>
                  <Badge variant="outline" className="w-fit text-xs font-medium text-muted-foreground border-border mb-2">
                    {pillar.tag}
                  </Badge>
                  <CardTitle className="text-xl font-semibold text-card-foreground font-default">
                    {pillar.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="p-6 md:p-8 pt-0 flex-grow flex flex-col justify-between">
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed mb-6 font-default">
                    {pillar.description}
                  </CardDescription>

                  <div className="pt-4 border-t border-border mt-auto">
                    <div className="flex items-center gap-2 text-xs font-medium text-foreground">
                      <CheckCircle2 className="size-4 text-primary shrink-0" />
                      <span>{pillar.deliverable}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>)}

          <div data-index={5} className="pillar-card h-full lg:col-span-1 md:col-span-2 lg:col-span-1">
            <div className="h-full rounded-xl bg-primary text-primary-foreground p-6 md:p-8 flex flex-col justify-between relative overflow-hidden shadow-md">
              <div className="relative z-10">
                <div className="p-3 rounded-lg bg-primary-foreground/10 text-primary-foreground w-fit mb-6">
                  <TrendingUp className="size-6" />
                </div>
                <Badge variant="outline" className="mb-3 text-xs border-primary-foreground/30 text-primary-foreground bg-primary-foreground/10">
                  Cross-Pillar Synergy
                </Badge>
                <h3 className="text-xl font-semibold mb-3 font-default">
                  Unified System Execution
                </h3>
                <p className="text-sm text-primary-foreground/85 leading-relaxed mb-6">
                  Every pillar is designed to reinforce the next. Technical crawlability elevates authoritative content, while strategic outreach accelerates national rankings across all target markets.
                </p>
              </div>

              <div className="relative z-10 pt-4 border-t border-primary-foreground/20">
                <Button asChild variant="secondary" className="w-full justify-between group font-medium text-secondary-foreground">
                  <Link to="/ContactPage">
                    <span>Discuss Your Strategy</span>
                    <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="interconnection-block rounded-2xl bg-muted/60 border border-border p-6 md:p-10">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider mb-2">
                <Layers className="size-4 text-accent" />
                <span>The Interconnected Flywheel</span>
              </div>
              <h3 className="text-2xl font-semibold text-foreground tracking-tight font-default">
                How These Pillars Work Together
              </h3>
              <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed">
                Isolated SEO efforts yield minimal results. By orchestrating all five pillars simultaneously, each improvement compounds to drive consistent, long-term search dominance.
              </p>
            </div>

            <div className="w-full lg:w-auto grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
              {connectionPoints.map((point, idx) => <div key={idx} data-index={idx} className="bg-card p-4 rounded-lg border border-border">
                  <div className="text-xs font-semibold text-foreground mb-1 font-default">
                    {point.title}
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal">
                    {point.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            Need clarity on which pillar is holding your organic growth back?
          </p>
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
            <Link to="/FreelanceSeoConsultantPage">
              <span>Book a Free SEO Consultation</span>
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}